// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — the HYBRID LEFT FUNCTION RAIL (Tier-3 fidelity overhaul).
// Mock: Tak-TDF COP.dc.html lines 123–141 (the 54px icon rail) + 863–866 (railBtn /
// pluginDot / requestBadge styles). Spec: docs/ux/SHELL-COMPOSITION.md §2.1–2.4.
//
// A 54px icon column that REPLACES the old 230px content gutter as the left rail.
// It is the navigation toolbar that SUMMONS the LeftSlideout panes; the heavy
// content (feeds, search, request, plugin panes, audit) lives in the slideout, not
// here. The rail is the leftGutter slot inside GutterFrame's grid, so collapse-to-
// bare (`\`) still zeroes it with the rest of the frame. A rail-bottom chevron
// EXPANDS it to icons+NAMES (an overlay widen via railState — the grid column and
// map box never reflow; state persists device-scope).
//
// THE HYBRID: the mock's rail is icons-only. We MIGRATE our lockstep-tested spine
// instruments INTO it without discarding behavior:
//   • VerdictGlyph  → a rail INSTRUMENT at the TOP (above search). Its drill-popover
//                     behavior is intact — it is our addition to the mock's icon set.
//   • search/layers/request/fires/ew/audit → rail buttons that open the slideout pane
//                     of the same name (LivingSpine is the "layers" pane content).
//   • ContributionAnchor → a subtle rail icon (the deferred safety-of-life slot is
//                     preserved, §2.4 — never dropped silently).
//   • PersonaSwitcher → the rail-bottom PROFILE icon (the persona popover + real
//                     idplite re-login are UNCHANGED — only the trigger affordance
//                     changes from a text chip to a 40×40 icon).
//
// The request (lock) button carries a teal COUNT badge wired to status.census.
// requestable (a forward-looking 0 placeholder today — the badge is hidden until it
// lights up; NO PDP/deny path). Plugin buttons (fires/ew) carry the teal plugin-dot.
//
// SVG icon paths are copied VERBATIM from the mock (lines 125–140). React stays OUT
// of the render loop: this only reads the immutable CopStatus + calls CopHandle /
// the orchestrator's panel/profile callbacks.

import type { ReactNode } from "react";
import type { CopStatus, CopHandle } from "../host/cop.ts";
import { VerdictGlyph } from "./VerdictGlyph.tsx";
import { ContributionAnchor } from "./ContributionAnchor.tsx";
import { PersonaSwitcher } from "./PersonaSwitcher.tsx";
import { claimHeavy } from "./heavyPanel.ts";
import { useRailExpanded, setRailExpanded } from "./railState.ts";
import "./functionrail.css";

/** The summonable slideout panes (null = closed). Mirrors the mock's leftPanel
 *  state (Tak-TDF COP.dc.html:864). The rail buttons set this via onOpenPanel.
 *  `chat` re-homes the encrypted mission-room chat into the LEFT slideout (a dedicated
 *  rail toggle), consistent with the other capability panes — instead of the stuck
 *  right-side PanelHost dock. */
export type LeftPanel = "search" | "layers" | "request" | "fires" | "ew" | "audit" | "chat" | "alerts" | null;

export interface FunctionRailProps {
  status: CopStatus;
  handle: CopHandle;
  /** The currently-open slideout pane (drives each button's active state). */
  leftPanel: LeftPanel;
  /** Toggle a slideout pane (clicking an already-open pane closes it). */
  onOpenPanel: (pane: Exclude<LeftPanel, null>) => void;
  /** PersonaSwitcher (rail-bottom Profile) wiring — unchanged re-login behavior. */
  personaCurrent: string;
  personaSummary: string;
  onSwitchPersona: (username: string, password: string) => void;
  onLogout: () => void;
}

/** The short NAME shown beside the icon when the rail is EXPANDED. Derived from the
 *  button's OWN `title` (the words already carried by every rail item's tooltip/aria
 *  — no parallel label list to drift): the segment before the " — " detail clause.
 *  "Chat — encrypted mission rooms" → "Chat"; a dash-less title is used whole. */
function railName(title: string): string {
  return title.split(" — ")[0].trim();
}

/** One icon rail button. `active` paints the teal selected state from the mock's
 *  railBtn(active) (line 863): teal-wash fill + teal hairline border, else
 *  transparent. `accent` glows the active border a plugin/accent hue. When the rail
 *  is expanded the button widens into an icon+NAME row (the label span below is
 *  hidden by CSS while collapsed) — one control, two densities. */
function RailButton({
  title,
  active,
  onClick,
  children,
  accent,
  disabled,
}: {
  title: string;
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  accent?: "fires" | "ew";
  /** Honestly DISABLE a rail action whose host-side path is not wired yet (no dead button:
   *  the control is visibly + semantically unavailable, not a click that silently does nothing). */
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      className="rail-btn"
      title={title}
      aria-label={title}
      aria-pressed={active}
      aria-disabled={disabled ? "true" : undefined}
      data-active={active ? "" : undefined}
      data-accent={accent}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
      {/* aria-hidden: the button already announces the FULL title; the visible
          name is presentation for the expanded rail only. */}
      <span className="rail-btn__label" aria-hidden="true">
        {railName(title)}
      </span>
    </button>
  );
}

export function FunctionRail({
  status,
  handle,
  leftPanel,
  onOpenPanel,
  personaCurrent,
  personaSummary,
  onSwitchPersona,
  onLogout,
}: FunctionRailProps): JSX.Element {
  // Forward-looking requestable count (always 0 today; the badge is hidden until it
  // lights up). Wired to the SAME Glomar-safe census the bottom pill reads — no PDP /
  // deny path. The badge shows ONLY when count>0 (mock line 866).
  const requestableCount = status.census.requestable;

  // EXPANDED rail (icons + NAMES): shared via railState so the LeftSlideout can
  // anchor beside the wider rail. Persisted device-scope (localStorage, "tak-tdf:"
  // key — see railState.ts for the local-settings fold-in note).
  const expanded = useRailExpanded();

  return (
    <nav className="rail" aria-label="Function rail" data-expanded={expanded ? "" : undefined}>
      {/* THE STATE-OF-THE-PICTURE INSTRUMENT (our hybrid addition). Drill-popover
          behavior intact — rendered in its compact rail-instrument variant. The
          expanded-rail caption names the instrument; its live state stays in the
          glyph's own title/aria (which carry the full words). */}
      <div className="rail__item">
        <VerdictGlyph status={status} handle={handle} variant="rail" />
        <span className="rail__item-label" aria-hidden="true">
          Verdict
        </span>
      </div>

      {/* search */}
      <RailButton title="Search" active={leftPanel === "search"} onClick={() => onOpenPanel("search")}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <line x1="16.5" y1="16.5" x2="21" y2="21" />
        </svg>
      </RailButton>

      {/* layers — opens the LivingSpine feed/catalog pane */}
      <RailButton title="Layers" active={leftPanel === "layers"} onClick={() => onOpenPanel("layers")}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M12 3 3 8l9 5 9-5-9-5Z" />
          <path d="M3 13l9 5 9-5" />
        </svg>
      </RailButton>

      {/* chat — the encrypted mission-room chat (re-homed into the LEFT slideout; a
          dedicated toggle consistent with the other panes, instead of the stuck right dock) */}
      <RailButton title="Chat — encrypted mission rooms" active={leftPanel === "chat"} onClick={() => onOpenPanel("chat")}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M4 5h16v11H8l-4 4V5Z" />
          <path d="M8 9.5h8M8 12.5h5" />
        </svg>
        {status.chatUnread > 0 && (
          <span className="rail__chat-badge" aria-hidden="true">
            {status.chatUnread > 99 ? "99+" : status.chatUnread}
          </span>
        )}
      </RailButton>

      {/* alerts — the acknowledgeable alert log (mark-read on click + clear-all). Reads
          status.alertLog (the host built it off the painted/visible set, so it is Glomar-safe). */}
      <RailButton title="Alerts — acknowledge + clear" active={leftPanel === "alerts"} onClick={() => onOpenPanel("alerts")}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </svg>
      </RailButton>

      {/* place waypoint (S3) — ARMS map placement; the next click drops a typed, fail-closed-
          classified waypoint that publishes through the projection seam + crosses to the TAK net. */}
      <RailButton
        title="Place waypoint — click the map to drop one at your working classification"
        active={false}
        onClick={() => handle.stageAction({ kind: "waypoint" })}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M12 21s7-6 7-12a7 7 0 1 0-14 0c0 6 7 12 7 12Z" />
          <circle cx="12" cy="9" r="2.3" />
        </svg>
      </RailButton>

      {/* call for evac — LIVE (2026-07-06): the human-gated dispatch is the evacRequest composer
          dialog (location + urgency + note, explicit submit), emitted as a life-safety alert that
          round-trips the TAK net. Location = own true position only while sharing is ON. */}
      <RailButton
        title="Call for evac — compose + dispatch a life-safety request"
        active={false}
        onClick={() => void handle.callForEvac()}
      >
        {/* MEDIC cross (CASEVAC/MEDEVAC) — the life-safety mark, not a generic plus. */}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <path d="M12 8.5v7M8.5 12h7" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      </RailButton>

      {/* 2.5D tilt — flip the camera between the flat plan-view scope and the tilted altitude view.
          Active = currently tilted (reads status.viewMode, kept honest by the pitchend listener, so
          a hand-tilt / Shift-drag lights it too). The ⌘K view-2d/view-25d commands share this seam. */}
      <RailButton
        title={status.viewMode === "2.5d" ? "2.5D tilt (on) — switch to 2D plan" : "2D plan — switch to 2.5D tilt"}
        active={status.viewMode === "2.5d"}
        onClick={() => handle.setViewMode(status.viewMode === "2.5d" ? "2d" : "2.5d")}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M12 2 21 7v10l-9 5-9-5V7l9-5Z" />
          <path d="M12 12v10M12 12 3 7M12 12l9-5" />
        </svg>
      </RailButton>

      <div className="rail__divider" />
      <div className="rail__plug-label">PLUG</div>

      {/* fires (plugin) */}
      <RailButton title="Call for Fires — plugin (not wired)" active={leftPanel === "fires"} accent="fires" disabled onClick={() => onOpenPanel("fires")}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M12 3c1 3-1 4-1 6a3 3 0 0 0 6 0c0-1 0-2-.5-3 2 1.5 3.5 4 3.5 7a8 8 0 1 1-16 0c0-4 3-6 4-9 .5 1.5 2 2 4 2Z" />
        </svg>
        <span className="rail__plugin-dot" aria-hidden="true" />
      </RailButton>

      {/* ew / spectrum (plugin) */}
      <RailButton title="Spectrum / EW — plugin (not wired)" active={leftPanel === "ew"} accent="ew" disabled onClick={() => onOpenPanel("ew")}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M5 5a10 10 0 0 1 14 0M7.5 7.5a6.5 6.5 0 0 1 9 0M10 10a3 3 0 0 1 4 0" />
          <circle cx="12" cy="13" r="1.4" fill="currentColor" stroke="none" />
        </svg>
        <span className="rail__plugin-dot" aria-hidden="true" />
      </RailButton>

      {/* the deferred safety-of-life slot, preserved as a subtle rail icon (§2.4).
          Caption reuses the anchor's own title word ("Contribute — deferred to 0.3b"). */}
      <div className="rail__item">
        <ContributionAnchor variant="rail" />
        <span className="rail__item-label" aria-hidden="true">
          Contribute
        </span>
      </div>

      <div className="rail__spacer" />

      {/* request access — discoverable datasets (Glomar: REQUESTABLE language only).
          The teal count badge shows ONLY when requestableCount>0 (mock line 866). */}
      <RailButton title="Request access — discoverable datasets" active={leftPanel === "request"} onClick={() => onOpenPanel("request")}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <rect x="5" y="11" width="14" height="9" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0" />
        </svg>
        {requestableCount > 0 && (
          <span className="rail__request-badge" aria-hidden="true">
            {requestableCount}
          </span>
        )}
      </RailButton>

      {/* audit & provenance */}
      <RailButton title="Audit &amp; provenance" active={leftPanel === "audit"} onClick={() => onOpenPanel("audit")}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M6 3h9l3 3v15H6Z" />
          <path d="M9 9h6M9 13h6M9 17h4" />
        </svg>
      </RailButton>

      {/* settings — SUMMONS the SettingsDrawer (display · symbology · layout · render ·
          motion). It claims the R-LAW-3 "settings" heavy slot, so opening it collapses any
          other heavy surface; the drawer self-manages open/close + Esc. No host plumbing,
          no commit, no security path — a pure display-preference surface (Glomar-safe). */}
      <RailButton title="Settings — identity · display · presence · render" active={false} onClick={() => claimHeavy("settings")}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
        </svg>
      </RailButton>

      <div className="rail__divider rail__divider--profile" />

      {/* rail-bottom PROFILE — the persona-switch affordance (icon trigger). The
          persona popover + the real idplite re-login are UNCHANGED. Caption reuses
          the trigger's own title word ("Profile — switch persona"). */}
      <div className="rail__item">
        <PersonaSwitcher
          mode="icon"
          current={personaCurrent}
          summary={personaSummary}
          onSwitch={onSwitchPersona}
          onLogout={onLogout}
        />
        <span className="rail__item-label" aria-hidden="true">
          Profile
        </span>
      </div>

      {/* EXPAND/COLLAPSE — the rail-bottom chevron. Collapsed = today's 54px icon
          rail; expanded = icons + NAMES (an overlay widen — the grid column and the
          map box do NOT reflow; the slideout shifts beside it via railState). */}
      <button
        type="button"
        className="rail-btn rail__expand"
        title={expanded ? "Collapse rail — icons only" : "Expand rail — show names"}
        aria-label={expanded ? "Collapse rail — icons only" : "Expand rail — show names"}
        aria-expanded={expanded}
        onClick={() => setRailExpanded(!expanded)}
      >
        <svg
          className="rail__expand-chevron"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          aria-hidden="true"
        >
          <path d="M6 6l6 6-6 6M13 6l6 6-6 6" />
        </svg>
        <span className="rail-btn__label" aria-hidden="true">
          Collapse
        </span>
      </button>
    </nav>
  );
}
